import { openConfirm, closeConfirm } from './views/farm/confirm-dialog';

/**
 * "Are you sure?" for leaving a view that is in the middle of something.
 *
 * The shell's `setTab` tears the current view down and builds the next one, with
 * no chance to object. That is right for almost everything and wrong for one
 * case: an arena SEARCH. Entering the arena spends a daily entry, and leaving the
 * queue gives the seat back but not the entry — so a player who clicked Shop to
 * check something mid-search lost the run and was told nothing at all. Owner:
 * "when I've started search on arena and I'm trying to switch tab, it should
 * show, you will lose your points."
 *
 * A view registers a guard while it has something to lose and clears it the
 * moment it does not. Deliberately a single slot rather than a list: only one
 * view is on screen, so a second registration is a leak from a torn-down view
 * rather than a second thing to ask about, and it should replace the first.
 *
 * The prompt is the game's own card, NOT `window.confirm` — owner, 2026-08-17:
 * "we make ingame confirmation, not via chrome", and views/farm/no-browser-dialogs
 * .test.ts enforces it. That makes the answer asynchronous, which is why
 * `confirmLeave` returns a promise and `setTab` has to await it rather than
 * branching on a boolean.
 */

let guard: { message: string } | null = null;

/** Ask before leaving the current view. Replaces any previous guard. */
export function setLeaveGuard(message: string): void {
  guard = { message };
}

/** Nothing left to lose — leaving is free again. */
export function clearLeaveGuard(): void {
  guard = null;
}

export const hasLeaveGuard = (): boolean => guard !== null;

/**
 * May the shell navigate away?
 *
 * Resolves immediately when nothing is at stake, so the common tab switch costs
 * nothing and does not wait a frame. When a guard is set it paints the card and
 * resolves on the player's answer; saying yes also clears the guard, because they
 * have accepted the loss and the view being left will never get another chance to
 * clear it. Cancelling leaves it armed — a "no" must not quietly disarm the
 * prompt for the next attempt.
 */
export function confirmLeave(): Promise<boolean> {
  const g = guard;
  if (!g) return Promise.resolve(true);
  return new Promise<boolean>((resolve) => {
    openConfirm({
      title: 'Leave the arena?',
      body: g.message,
      confirmLabel: 'Leave anyway',
      cancelLabel: 'Stay',
      onConfirm: () => { guard = null; resolve(true); },
      // Escape, the backdrop and Cancel all land here. Without it the shell would
      // wait on a promise that never settles and the tab buttons would stop
      // working — a worse failure than the one this dialog prevents.
      onCancel: () => resolve(false),
    });
  });
}

/** Test seam: drop the guard and any card it painted. */
export function __resetLeaveGuard(): void {
  guard = null;
  closeConfirm();
}
